import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import _ from 'lodash';
import FormControl from 'react-bootstrap/lib/FormControl';
import ControlLabel from 'react-bootstrap/lib/ControlLabel';
import { Record } from 'immutable';

import { translate as t } from '../../lib/I18n';
import { KeyCodes } from '../../constants/AppConstants';

import focusableWrapper from './FocusableWrapper';
import Icon from '../general/Icon';
import Checkbox from './Checkbox';
import Select from './Select';
import Radio from './Radio';

function _getValueByType (type, target) {
    const valueTypes = {
        text: target.value,
        number: target.value,
        checkbox: target.checked,
        radio: target.checked
    };
    return type in valueTypes ? valueTypes[type] : valueTypes.text;
}

function _getLabelByType (type, label, required) {
    const labelTypes = {
        text: required ? label + t('required') : label,
        checkbox: label,
        select: label,
        radio: label
    };
    return type in labelTypes ? labelTypes[type] : labelTypes.text;
}

const wrappedCheckbox = focusableWrapper(Checkbox);
const wrappedSelect = focusableWrapper(Select);
const wrappedFormControl = focusableWrapper(FormControl);
const wrappedRadio = focusableWrapper(Radio);

class FormField extends React.PureComponent {

    static propTypes = {
        autoFocus: PropTypes.bool,
        checked: PropTypes.bool,
        children: PropTypes.any,
        className: PropTypes.string,
        disabled: PropTypes.bool,
        externalFormGroup: PropTypes.bool,
        field: PropTypes.string.isRequired,
        focused: PropTypes.bool,
        form: PropTypes.oneOfType([
            PropTypes.instanceOf(Record),
            PropTypes.object
        ]).isRequired,
        hideOptional: PropTypes.bool,
        id: PropTypes.string,
        index: PropTypes.number,
        indicator: PropTypes.element,
        isPasswordShown: PropTypes.bool,
        label: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.node
        ]),
        labelClassName: PropTypes.string,
        labelText: PropTypes.string,
        noFloatyPlaceholder: PropTypes.bool,
        onBlur: PropTypes.func,
        onChange: PropTypes.func,
        onFocus: PropTypes.func,
        outsideLabel: PropTypes.bool,
        placeholder: PropTypes.string,
        required: PropTypes.bool,
        rows: PropTypes.number,
        showPlaceholder: PropTypes.bool,
        showRequired: PropTypes.bool,
        type: PropTypes.string,
        avoidPaste: PropTypes.bool,
        value: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool
        ])
    };

    static defaultProps = {
        autoFocus: false,
        checked: false,
        disabled: false,
        externalFormGroup: false,
        focused: false,
        index: null,
        isPasswordShown: false,
        label: '',
        labelText: null,
        noFloatyPlaceholder: false,
        onBlur: _.noop,
        onChange: _.noop,
        onFocus: _.noop,
        outsideLabel: false,
        placeholder: null,
        required: false,
        type: 'text',
        avoidPaste: false,
    };

    state = (() => (
        {
            value: this.props.value === undefined ? this.props.form[this.props.field] : this.props.value
        }
    ))();

    UNSAFE_componentWillReceiveProps (nextProps) {
        // on IE, onChange event might come after actual value of an element
        // have changed. We detect this and render element as-is, hoping that
        // next onChange will eventually come and bring our modifications anyways.
        // Ignoring this causes skipped letters in controlled components
        // https://github.com/r0man/sablono/commit/579f3d77271afd9edacc4fa0813f93ff42fe33a6
        if (!['checkbox', 'select'].includes(this.props.type)) {
            const { value: nodeValue } = ReactDOM.findDOMNode(this.inputElement);
            const stateValue = _.isString(this.state.value) ? this.state.value : '';
            if (stateValue !== nodeValue) {
                this.setState({ value: nodeValue });
                return;
            }
        }

        const current = this.props.form[this.props.field];
        const next = nextProps.value === undefined ? nextProps.form[this.props.field] : nextProps.value;

        if (current !== next) {
            this.setState({ value: next });
        }
    }

    _handleChange = ({ target }) => {
        // Set up a debounced change handler, to avoid a callback on every keystroke
        if (!this.parentChangeHandler) {
            this.parentChangeHandler = _.debounce(this.props.onChange, 300, { leading: true });
        }

        const value = _getValueByType(this.props.type, target);
        this.setState({ value });
        if (this.props.index !== null && this.props.index >= 0) {
            this.parentChangeHandler(this.props.field, value, this.props.index);
        } else {
            this.parentChangeHandler(this.props.field, value);
        }
    };

    _handlePaste = (e) => {
        if (this.props.avoidPaste) {
            e.preventDefault();
        } else {
            this._handleChange(e);
        }
    };

    _handleKeyDown = (event) => {
        if (event.keyCode === KeyCodes.ENTER) {
            if (this.props.index !== null && this.props.index >= 0) {
                this.props.onChange(this.props.field, this.state.value, this.props.index);
            } else {
                this.props.onChange(this.props.field, this.state.value);
            }
        }
    };

    _handleBlur = () => {
        if (this.props.index !== null && this.props.index >= 0) {
            this.props.onBlur(this.props.field, this.state.value, this.props.index);
        } else {
            this.props.onBlur(this.props.field, this.state.value);
        }
    };

    _handleFocus = () => {
        if (this.props.index !== null && this.props.index >= 0) {
            this.props.onFocus(this.props.field, this.state.value, this.props.index);
        } else {
            this.props.onFocus(this.props.field, this.state.value);
        }
    };

    _renderPlaceholder() {
        const { placeholder, noFloatyPlaceholder, outsideLabel } = this.props;
        const { value } = this.state;

        if (value && placeholder && !noFloatyPlaceholder && !outsideLabel) {
            return <span className="placeholder text-sm">{ placeholder }</span>;
        }
        return null;
    }

    render () {
        const {
            children,
            className,
            externalFormGroup,
            field,
            focused,
            hideOptional,
            indicator,
            label,
            labelText,
            labelClassName,
            outsideLabel,
            placeholder,
            required,
            showPlaceholder,
            showRequired,
            type,
        } = this.props;
        const typeLabel = _getLabelByType(type, label, required);
        let props = _.pick(this.props, [
            'id',
            'placeholder',
            'disabled',
            'checked',
            'required',
            'value',
            'type',
            'groupClassName',
            'labelClassName',
            'autoFocus',
            'maxLength',
            'autoComplete',
            'aria-describedby',
            'aria-invalid',
            'data-test-id',
        ]);
        if (!props.id) { props.id = _.uniqueId(field); }

        let Element;
        let classNames = className;
        let separateLabel = false;
        let noLabel = false;
        const omittedProps = ['labelClassName'];

        if (outsideLabel) {
            omittedProps.push('placeholder');
        }

        switch (type) {
            case 'checkbox':
                Element = wrappedCheckbox;
                break;
            case 'select':
                Element = wrappedSelect;
                noLabel = true;
                break;
            case 'radio':
                Element = wrappedRadio;
                break;
            default:
                Element = wrappedFormControl;
                classNames = classnames('input-field', classNames);
                separateLabel = !outsideLabel;

                props = _.omit(props, omittedProps);
                break;
        }

        if (type === 'textarea') {
            Object.assign(props, {
                componentClass: type,
                rows: this.props.rows
            });
        }

        const placeholderProp = showPlaceholder ? { placeholder } : null;

        return (
            <React.Fragment>
                {outsideLabel && (
                    <label htmlFor={props.id} aria-hidden="true" className="outside-label">
                        {labelText || label}{' '}
                        {required && <span className="sr-only">{t('required')}</span>}
                        {!required && !hideOptional && (
                            <span className="outside-label__optional">{t('form.optional')}</span>
                        )}
                    </label>
                )}
                <div
                    className={classnames('form-field', {
                        'form-group': !externalFormGroup,
                        'form-field--outside-label': outsideLabel,
                        'form-field--with-indicator': Boolean(indicator),
                    }, indicator && classNames)}
                >
                    <Element
                        {...props}
                        ref={el => {
                            this.inputElement = el;
                        }}
                        className={!indicator && classNames}
                        value={this.state.value || ''}
                        onChange={this._handleChange}
                        label={noLabel ? null : !separateLabel ? typeLabel : null}
                        onKeyDown={this._handleKeyDown}
                        onBlur={this._handleBlur}
                        focused={focused}
                        onFocus={this._handleFocus}
                        onPaste={this._handlePaste}
                        {...placeholderProp}
                    >
                        {children && children}
                    </Element>

                    {typeLabel && separateLabel && (
                        <ControlLabel srOnly htmlFor={props.id} className={labelClassName}>
                            {label}
                        </ControlLabel>
                    )}
                    {(required || showRequired) && <Icon icon="mandatory" />}
                    {indicator}
                    {this._renderPlaceholder()}
                </div>
            </React.Fragment>
        );
    }

}


export default FormField;
